import orderBy from 'lodash/orderBy';
import React, { useMemo } from 'react';
import { ChartCard, generateChartColors, InfoTooltip, PieChart } from 'ui-v2';

import { type FragmentType, gql, useFragment } from '@/generated';
import { useChartTranslations } from '@/hooks/useChartTranslations';

export const Index_HealthcareTypeFragment = gql(/* GraphQL */ `
  fragment Index_HealthcareTypeFragment on HealthcareIndicators {
    productType {
      items {
        count
        label
        pct
      }
      isDataSuppressed
    }
  }
`);

type HealthcareTypeProps = {
  className?: string;
  indicators: FragmentType<typeof Index_HealthcareTypeFragment>;
};

export function HealthcareType({ indicators, className }: HealthcareTypeProps) {
  const { productType } = useFragment(Index_HealthcareTypeFragment, indicators);

  const { title, subtitle, chartTooltip, getOptionLabel, getOptionTooltip, getOptionDisplayValue } =
    useChartTranslations('healthcareType');

  const pieData = useMemo(() => {
    const colors = generateChartColors(productType.items.length);
    const sorted = orderBy(
      productType.items
        .filter((item) => !!item)
        .map((item) => ({
          label: getOptionLabel(item?.label),
          tooltipLabel: getOptionTooltip(item?.label),
          value: item?.pct,
          displayValue: getOptionDisplayValue(item.count, {
            pct: item.pct.toLocaleString('en', { maximumFractionDigits: 1 }),
          }),
        })),
      ['value'],
      ['desc']
    );
    return sorted.map((x, i) => ({ ...x, color: colors[i] }));
  }, [productType.items]);

  return (
    <ChartCard className={className}>
      <ChartCard.Header>
        <ChartCard.Title>
          {title}
          {chartTooltip && <InfoTooltip>{chartTooltip}</InfoTooltip>}
        </ChartCard.Title>
        {subtitle && <ChartCard.Subtitle>{subtitle}</ChartCard.Subtitle>}
      </ChartCard.Header>
      <ChartCard.Body isDataSuppressed={productType.isDataSuppressed}>
        <PieChart data={pieData} showLegend chartPosition="right" />
      </ChartCard.Body>
    </ChartCard>
  );
}
